import React, { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { User, Mail, Phone, Lock, Eye, EyeOff, ArrowLeft, KeyRound } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { toast } from 'sonner';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const CustomerAuth = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const resetToken = searchParams.get('reset_token');
  
  const [isLogin, setIsLogin] = useState(true);
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [showResetPassword, setShowResetPassword] = useState(!!resetToken);
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const [loginData, setLoginData] = useState({
    email: '',
    password: ''
  });

  const [registerData, setRegisterData] = useState({
    name: '',
    email: '',
    phone: '',
    password: '',
    confirmPassword: ''
  });

  const [forgotEmail, setForgotEmail] = useState('');
  const [resetData, setResetData] = useState({
    token: resetToken || '',
    password: '',
    confirmPassword: ''
  });

  const handleLoginChange = (field, value) => {
    setLoginData(prev => ({ ...prev, [field]: value }));
  };

  const handleRegisterChange = (field, value) => {
    setRegisterData(prev => ({ ...prev, [field]: value }));
  };

  const handleForgotPassword = async (e) => {
    e.preventDefault();
    
    if (!forgotEmail) {
      toast.error('Please enter your email address');
      return;
    }

    setIsLoading(true);
    try {
      const response = await axios.post(`${API}/customers/forgot-password`, { email: forgotEmail });
      
      if (response.data.success) {
        toast.success(response.data.message);
        setShowForgotPassword(false);
        setForgotEmail('');
      } else {
        toast.error(response.data.message || 'Failed to process request');
      }
    } catch (error) {
      console.error('Forgot password error:', error);
      toast.error('Failed to process request. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleResetPassword = async (e) => {
    e.preventDefault();
    
    if (!resetData.token || !resetData.password) {
      toast.error('Please fill in all fields');
      return;
    }

    if (resetData.password !== resetData.confirmPassword) {
      toast.error('Passwords do not match');
      return;
    }

    if (resetData.password.length < 6) {
      toast.error('Password must be at least 6 characters');
      return;
    }

    setIsLoading(true);
    try {
      const response = await axios.post(`${API}/customers/reset-password`, {
        token: resetData.token,
        password: resetData.password
      });
      
      if (response.data.success) {
        toast.success(response.data.message);
        setShowResetPassword(false);
        setResetData({ token: '', password: '', confirmPassword: '' });
        // Remove token from URL
        navigate('/login', { replace: true });
      } else {
        toast.error(response.data.message || 'Failed to reset password');
      }
    } catch (error) {
      console.error('Reset password error:', error);
      toast.error(error.response?.data?.detail || 'Failed to reset password. The link may have expired.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    
    if (!loginData.email || !loginData.password) {
      toast.error('Please fill in all fields');
      return;
    }

    setIsLoading(true);
    try {
      const response = await axios.post(`${API}/customers/login`, loginData);
      
      if (response.data.success) {
        // Store customer data and token
        sessionStorage.setItem('customer', JSON.stringify(response.data.customer));
        sessionStorage.setItem('customerToken', response.data.token);
        
        toast.success('Welcome back!');
        navigate('/account');
      } else {
        toast.error(response.data.message || 'Login failed');
      }
    } catch (error) {
      console.error('Login error:', error);
      toast.error(error.response?.data?.message || 'Login failed. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleRegister = async (e) => {
    e.preventDefault();
    
    if (!registerData.name || !registerData.email || !registerData.phone || !registerData.password) {
      toast.error('Please fill in all required fields');
      return;
    }

    if (registerData.password !== registerData.confirmPassword) {
      toast.error('Passwords do not match');
      return;
    }

    if (registerData.password.length < 6) {
      toast.error('Password must be at least 6 characters');
      return;
    }

    setIsLoading(true);
    try {
      const response = await axios.post(`${API}/customers/register`, {
        name: registerData.name,
        email: registerData.email,
        phone: registerData.phone,
        password: registerData.password
      });
      
      if (response.data.success) {
        // Store customer data and token
        sessionStorage.setItem('customer', JSON.stringify(response.data.customer));
        sessionStorage.setItem('customerToken', response.data.token);
        
        toast.success('Account created successfully!');
        navigate('/account');
      } else {
        toast.error(response.data.message || 'Registration failed');
      }
    } catch (error) {
      console.error('Registration error:', error);
      toast.error(error.response?.data?.message || 'Registration failed. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-teal-50 flex items-center justify-center p-4">
      <Card className="max-w-md w-full shadow-2xl">
        <CardHeader className="text-center bg-gradient-to-r from-teal-600 to-cyan-600 text-white rounded-t-lg relative">
          <Button
            variant="ghost"
            size="sm"
            className="absolute left-4 top-4 text-white hover:bg-white/20"
            onClick={() => {
              if (showForgotPassword || showResetPassword) {
                setShowForgotPassword(false);
                setShowResetPassword(false);
              } else {
                navigate('/');
              }
            }}
          >
            <ArrowLeft size={20} />
          </Button>
          <CardTitle className="text-2xl font-bold">
            {showResetPassword 
              ? 'Reset Password' 
              : showForgotPassword 
                ? 'Forgot Password' 
                : isLogin 
                  ? 'Welcome Back' 
                  : 'Create Account'
            }
          </CardTitle>
          <CardDescription className="text-teal-50">
            {showResetPassword 
              ? 'Enter your new password below'
              : showForgotPassword 
                ? 'Enter your email to reset your password'
                : isLogin 
                  ? 'Sign in to access your account and loyalty rewards' 
                  : 'Join us and start earning rewards with every wash'
            }
          </CardDescription>
        </CardHeader>
        
        <CardContent className="p-6">
          {/* Reset Password Form */}
          {showResetPassword ? (
            <form onSubmit={handleResetPassword} className="space-y-4">
              <div className="text-center mb-4">
                <KeyRound className="mx-auto text-teal-600 mb-2" size={48} />
                <p className="text-sm text-gray-600">Create a new password for your account</p>
              </div>

              {!resetToken && (
                <div className="space-y-2">
                  <label className="text-sm font-medium text-gray-700">Reset Token</label>
                  <Input
                    type="text"
                    placeholder="Enter reset token"
                    value={resetData.token}
                    onChange={(e) => setResetData(prev => ({ ...prev, token: e.target.value }))}
                    className="border-gray-300 focus:border-teal-500"
                  />
                </div>
              )}

              <div className="space-y-2">
                <label className="text-sm font-medium text-gray-700 flex items-center gap-2">
                  <Lock size={16} className="text-teal-600" />
                  New Password
                </label>
                <div className="relative">
                  <Input
                    type={showPassword ? 'text' : 'password'}
                    placeholder="Min 6 characters"
                    value={resetData.password}
                    onChange={(e) => setResetData(prev => ({ ...prev, password: e.target.value }))}
                    className="border-gray-300 focus:border-teal-500 pr-10"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700"
                  >
                    {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                  </button>
                </div>
              </div>

              <div className="space-y-2">
                <label className="text-sm font-medium text-gray-700 flex items-center gap-2">
                  <Lock size={16} className="text-teal-600" />
                  Confirm New Password
                </label>
                <Input
                  type={showPassword ? 'text' : 'password'}
                  placeholder="Confirm your password"
                  value={resetData.confirmPassword}
                  onChange={(e) => setResetData(prev => ({ ...prev, confirmPassword: e.target.value }))}
                  className="border-gray-300 focus:border-teal-500"
                />
              </div>

              <Button
                type="submit"
                disabled={isLoading}
                className="w-full bg-teal-600 hover:bg-teal-700 text-white py-5"
              >
                {isLoading ? 'Resetting...' : 'Reset Password'}
              </Button>

              <div className="text-center">
                <button
                  type="button"
                  onClick={() => {
                    setShowResetPassword(false);
                    navigate('/login', { replace: true });
                  }}
                  className="text-sm text-teal-600 hover:text-teal-700"
                >
                  Back to Login
                </button>
              </div>
            </form>
          ) : showForgotPassword ? (
            /* Forgot Password Form */
            <form onSubmit={handleForgotPassword} className="space-y-4">
              <div className="text-center mb-4">
                <Mail className="mx-auto text-teal-600 mb-2" size={48} />
                <p className="text-sm text-gray-600">
                  Enter your email address and we&apos;ll help you reset your password.
                </p>
              </div>

              <div className="space-y-2">
                <label className="text-sm font-medium text-gray-700 flex items-center gap-2">
                  <Mail size={16} className="text-teal-600" />
                  Email Address
                </label>
                <Input
                  type="email"
                  placeholder="your@email.com"
                  value={forgotEmail}
                  onChange={(e) => setForgotEmail(e.target.value)}
                  className="border-gray-300 focus:border-teal-500"
                />
              </div>

              <Button
                type="submit"
                disabled={isLoading}
                className="w-full bg-teal-600 hover:bg-teal-700 text-white py-5"
              >
                {isLoading ? 'Sending...' : 'Request Password Reset'}
              </Button>

              <div className="text-center">
                <button
                  type="button"
                  onClick={() => setShowForgotPassword(false)}
                  className="text-sm text-teal-600 hover:text-teal-700"
                >
                  Back to Login
                </button>
              </div>

              <div className="mt-4 p-4 bg-blue-50 rounded-lg border border-blue-200">
                <p className="text-sm text-blue-700">
                  <strong>Note:</strong> After requesting a reset, please contact the business owner to receive your reset link.
                </p>
              </div>
            </form>
          ) : isLogin ? (
            // Login Form
            <form onSubmit={handleLogin} className="space-y-4">
              <div className="space-y-2">
                <label className="text-sm font-medium text-gray-700 flex items-center gap-2">
                  <Mail size={16} className="text-teal-600" />
                  Email Address
                </label>
                <Input
                  type="email"
                  placeholder="your@email.com"
                  value={loginData.email}
                  onChange={(e) => handleLoginChange('email', e.target.value)}
                  className="border-gray-300 focus:border-teal-500"
                />
              </div>

              <div className="space-y-2">
                <label className="text-sm font-medium text-gray-700 flex items-center gap-2">
                  <Lock size={16} className="text-teal-600" />
                  Password
                </label>
                <div className="relative">
                  <Input
                    type={showPassword ? 'text' : 'password'}
                    placeholder="Enter your password"
                    value={loginData.password}
                    onChange={(e) => handleLoginChange('password', e.target.value)}
                    className="border-gray-300 focus:border-teal-500 pr-10"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700"
                  >
                    {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                  </button>
                </div>
              </div>

              <Button
                type="submit"
                disabled={isLoading}
                className="w-full bg-teal-600 hover:bg-teal-700 text-white py-5"
              >
                {isLoading ? 'Signing in...' : 'Sign In'}
              </Button>

              {/* Forgot Password Link */}
              <div className="text-center">
                <button
                  type="button"
                  onClick={() => setShowForgotPassword(true)}
                  className="text-sm text-teal-600 hover:text-teal-700 hover:underline"
                >
                  Forgot your password?
                </button>
              </div>
            </form>
          ) : (
            // Register Form
            <form onSubmit={handleRegister} className="space-y-4">
              <div className="space-y-2">
                <label className="text-sm font-medium text-gray-700 flex items-center gap-2">
                  <User size={16} className="text-teal-600" />
                  Full Name *
                </label>
                <Input
                  type="text"
                  placeholder="John Doe"
                  value={registerData.name}
                  onChange={(e) => handleRegisterChange('name', e.target.value)}
                  className="border-gray-300 focus:border-teal-500"
                />
              </div>

              <div className="space-y-2">
                <label className="text-sm font-medium text-gray-700 flex items-center gap-2">
                  <Mail size={16} className="text-teal-600" />
                  Email Address *
                </label>
                <Input
                  type="email"
                  placeholder="your@email.com"
                  value={registerData.email}
                  onChange={(e) => handleRegisterChange('email', e.target.value)}
                  className="border-gray-300 focus:border-teal-500"
                />
              </div>

              <div className="space-y-2">
                <label className="text-sm font-medium text-gray-700 flex items-center gap-2">
                  <Phone size={16} className="text-teal-600" />
                  Phone Number *
                </label>
                <Input
                  type="tel"
                  placeholder="(555) 123-4567"
                  value={registerData.phone}
                  onChange={(e) => handleRegisterChange('phone', e.target.value)}
                  className="border-gray-300 focus:border-teal-500"
                />
              </div>

              <div className="space-y-2">
                <label className="text-sm font-medium text-gray-700 flex items-center gap-2">
                  <Lock size={16} className="text-teal-600" />
                  Password *
                </label>
                <div className="relative">
                  <Input
                    type={showPassword ? 'text' : 'password'}
                    placeholder="Min 6 characters"
                    value={registerData.password}
                    onChange={(e) => handleRegisterChange('password', e.target.value)}
                    className="border-gray-300 focus:border-teal-500 pr-10"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700"
                  >
                    {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                  </button>
                </div>
              </div>

              <div className="space-y-2">
                <label className="text-sm font-medium text-gray-700 flex items-center gap-2">
                  <Lock size={16} className="text-teal-600" />
                  Confirm Password *
                </label>
                <Input
                  type={showPassword ? 'text' : 'password'}
                  placeholder="Confirm your password"
                  value={registerData.confirmPassword}
                  onChange={(e) => handleRegisterChange('confirmPassword', e.target.value)}
                  className="border-gray-300 focus:border-teal-500"
                />
              </div>

              <Button
                type="submit"
                disabled={isLoading}
                className="w-full bg-teal-600 hover:bg-teal-700 text-white py-5"
              >
                {isLoading ? 'Creating Account...' : 'Create Account'}
              </Button>
            </form>
          )}

          {/* Toggle between login and register */}
          <div className="mt-6 text-center">
            <p className="text-gray-600">
              {isLogin ? "Don't have an account?" : "Already have an account?"}
              <button
                type="button"
                onClick={() => setIsLogin(!isLogin)}
                className="ml-2 text-teal-600 hover:text-teal-700 font-semibold"
              >
                {isLogin ? 'Sign Up' : 'Sign In'}
              </button>
            </p>
          </div>

          {/* Guest option */}
          <div className="mt-4 pt-4 border-t text-center">
            <p className="text-sm text-gray-500 mb-2">Or continue without an account</p>
            <Button
              variant="outline"
              onClick={() => navigate('/#book-now')}
              className="w-full border-teal-600 text-teal-600 hover:bg-teal-50"
            >
              Book as Guest
            </Button>
          </div>

          {/* Loyalty info */}
          <div className="mt-6 bg-teal-50 rounded-lg p-4 border border-teal-200">
            <h4 className="font-semibold text-teal-800 mb-2">🎁 Loyalty Rewards</h4>
            <p className="text-sm text-teal-700">
              Create an account to track your washes. After 10 services, your 11th wash is FREE!
            </p>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default CustomerAuth;
